import Buttons from "base/basicComponents/button";
import NormalToolTip from "base/basicComponents/toolTip";
import { ChangeEvent, useRef, useState } from "react";
import { useHover, useSetState, useRequest, useDebounceEffect } from "ahooks";
import "./searchAndColumnSelect.scss";
import { create } from "zustand";
import { Button, Checkbox, message, Modal, Spin } from "antd";
import InputLabel from "base/basicComponents/inputLabel";
import NormalSelect from "base/basicComponents/select";
import toDoListService from "service/toDoListService";
import { cn, isAsAndAbove, reloadMessage } from "utils/index";
import { NormalTextArea } from "base/basicComponents/input";
import TopFilterBase from "app/commonComponents/listBase/TopFilterBase";
import AppSetting from "config/AppSetting";
import { ToDoPendingTableRow } from "app/pages/toDoList/types";
import { BatchDelegateStore, DelegateToState, MessageState } from "./types";
import { userRoles } from "app/publicDictionaryValues";
import { useLocation } from "react-router-dom";
import { isArray, isEqual, sortBy } from "lodash-es";
import batchDelegateDisableIcon from "assets/image/batchDelegateDisableIcon.svg";
import batchDelegateIcon from "assets/image/batchDelegateIcon.svg";

const asAndAboveRolesList = [
  userRoles.Manager,
  userRoles.Assistant,
  userRoles.TeamLeader,
  userRoles.Specialist,
];
const { sortList, handleFilterOption } = TopFilterBase();
const currentUser = AppSetting.localStorageValue.user.get();

const BatchDelegateButton = ({ tab }: { tab: number | undefined }) => {
  const location = useLocation();
  const handleClick = () => {
    setShowDelegatePostModal(true);
  };
  const [showDelegatePostModal, setShowDelegatePostModal] = useState(false);
  const selectedRows = useBatchDelegate((state) => state.selectedRows);
  const isAsAndAbove = useBatchDelegate((state) => state.isAsAndAbove);

  const isFromDiffAccount =
    selectedRows.length > 0 &&
    selectedRows.some((item) => item.currentNodeAccountId !== selectedRows[0].currentNodeAccountId);
  const isDisabled = selectedRows.length === 0 || isFromDiffAccount;
  const showButton = tab === 0 && location.pathname.startsWith("/ToDoList") && isAsAndAbove;
  if (!showButton) return null;
  return (
    <>
      <Buttons
        color="white-grey-line"
        size="small"
        btnClassName="!w-[9.25rem] !px-[0.875rem] !py-[0.5625rem] flex items-center justify-between text-[#333] text-sm leading-4 hover:text-[#3971ca] disabled:text-[#b3b3b3] disabled:bg-[#f5f5f5] disabled:cursor-not-allowed"
        onClick={handleClick}
        disabled={isDisabled}
        testid="batch-delegate-btn"
      >
        <img
          className="!m-0"
          src={isDisabled ? batchDelegateDisableIcon : batchDelegateIcon}
          alt="batch Delegate Icon"
        />
        <span>Batch Delegate</span>
      </Buttons>
      <DelegateComfirmModal
        isModalOpen={showDelegatePostModal}
        setModalOpen={setShowDelegatePostModal}
      />
    </>
  );
};

const SelectAllCheckbox = () => {
  const ref = useRef(null);
  const isHovering = useHover(ref);
  const selectedRows = useBatchDelegate((state) => state.selectedRows);
  const allTableRows = useBatchDelegate((state) => state.allTableRows);
  const isAsAndAbove = useBatchDelegate((state) => state.isAsAndAbove);

  const selectedRowsCanDelegate = selectedRows.filter((i) => i.isAllowDelegate);
  const allTableRowsCanDelegate = allTableRows.filter((i) => i.isAllowDelegate);

  const checked =
    selectedRowsCanDelegate.length === allTableRowsCanDelegate.length &&
    selectedRowsCanDelegate.length > 0;
  const indeterminate =
    selectedRowsCanDelegate.length > 0 &&
    selectedRowsCanDelegate.length < allTableRowsCanDelegate.length;
  const isFromDiffAccount =
    allTableRows.length > 0 &&
    allTableRows.some((item) => item.currentNodeAccountId !== allTableRows[0].currentNodeAccountId);
  const isAllRowDisabled = allTableRows.every((i) => !i.isAllowDelegate);
  return (
    <NormalToolTip
      element={
        <span ref={ref}>
          <Checkbox
            indeterminate={indeterminate}
            disabled={isFromDiffAccount || isAllRowDisabled}
            checked={checked}
            onChange={(event) => {
              const checked = event.target.checked;
              useBatchDelegate.setState({
                selectedRowKeys: checked
                  ? allTableRows.filter((i) => i.isAllowDelegate).map((i) => i.procedureStepId)
                  : [],
                selectedRows: checked ? allTableRows.filter((i) => i.isAllowDelegate) : [],
              });
            }}
          />
        </span>
      }
      placement="top"
      title={
        <span style={{ display: "inline-block", width: "16.75rem" }}>
          Only to-do items from the same account can be delegated together.
        </span>
      }
      open={isFromDiffAccount && isHovering && isAsAndAbove}
      overlayStyle={{ maxWidth: "fit-content" }}
    />
  );
};

const useBatchDelegate = create<BatchDelegateStore>((set, get) => {
  const onChange = (selectedRowKeys: string[], selectedRows: ToDoPendingTableRow[]) =>
    set({ selectedRowKeys, selectedRows });

  const rowSelection = {
    type: "checkbox",
    fixed: true,
    onChange,
    columnTitle: <SelectAllCheckbox />,
    getCheckboxProps: (record: ToDoPendingTableRow) => {
      const selectedRows = get().selectedRows;
      const isDiffAccountDisabled =
        selectedRows.length > 0 &&
        selectedRows[0].currentNodeAccountId !== record.currentNodeAccountId;
      return {
        disabled: !record.isAllowDelegate || isDiffAccountDisabled,
      };
    },
    renderCell: (
      checked: boolean,
      record: ToDoPendingTableRow,
      index: number,
      originNode: React.ReactNode
    ) => {
      const selectedRows = get().selectedRows;
      const isDiffAccountDisabled =
        selectedRows.length > 0 &&
        selectedRows[0].currentNodeAccountId !== record.currentNodeAccountId;

      if (isDiffAccountDisabled && record.isAllowDelegate) {
        return (
          <NormalToolTip
            element={originNode}
            placement="topLeft"
            title={
              <span style={{ display: "inline-block", width: "16.75rem" }}>
                Only to-do items from the same account can be delegated together.
              </span>
            }
            overlayStyle={{ maxWidth: "fit-content" }}
          />
        );
      }
      return originNode;
    },
  };

  return {
    isAsAndAbove: isAsAndAbove(),
    // selectedRowKeys is procedureStepId list
    selectedRowKeys: [],
    selectedRows: [],
    allTableRows: [],
    flushTableListDueToDelegate: {},
    setFlushTable: () => set({ flushTableListDueToDelegate: {} }),
    setAllTableRows: (allTableRows, roleList) => {
      const { roles: localStorageRoleList, ...rest } = AppSetting.localStorageValue.user.get();
      if (
        isArray(roleList) &&
        isArray(localStorageRoleList) &&
        !isEqual(sortBy(roleList, ["projectID"]), sortBy(localStorageRoleList, ["projectID"]))
      ) {
        AppSetting.localStorageValue.user.set({ ...rest, roles: roleList });
      }
      set({
        allTableRows,
        selectedRowKeys: [],
        selectedRows: [],
        ...(isArray(roleList) && {
          isAsAndAbove:
            roleList!
              .map((i) => i.roleID)
              .findIndex(
                (roleItem) => asAndAboveRolesList.findIndex((item) => item === roleItem) !== -1
              ) > -1,
        }),
      });
    },
    rowSelection,
    clearBatchDelegateInfo: () => set({ selectedRowKeys: [], selectedRows: [], allTableRows: [] }),
  };
});

const initDelegateToState = {
  loading: false,
  open: false,
  delegateToUserId: "",
  reasonText: "",
  reasonTextStatus: "",
  optionList: [],
};
const initMessageState = {
  showPostSubmitMessage: false,
  successFlag: false,
  failItems: [],
};
const initConfirmModalState = {
  showConfirmModal: false,
  modalContent: "",
  continue: false,
};

const DelegateComfirmModal = ({
  isModalOpen,
  setModalOpen,
}: {
  isModalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [loading, setLoading] = useState(false);
  const selectedRowKeys = useBatchDelegate((state) => state.selectedRowKeys);
  const selectedRows = useBatchDelegate((state) => state.selectedRows);

  const [delegateToState, setDelegateToState] = useSetState<DelegateToState>({
    ...initDelegateToState,
  });
  const [messageState, setMessageState] = useSetState<MessageState>({ ...initMessageState });

  const [confirmModalState, setConfirmModalState] = useSetState({ ...initConfirmModalState });

  const { run: runGetDelegateToList } = useRequest(toDoListService.getMultipleDelegateToList, {
    manual: true,
    debounceWait: 300,
    debounceLeading: true,
    debounceTrailing: false,
    onBefore: () => setDelegateToState({ loading: true }),
    onSuccess: (list) => {
      setDelegateToState({
        optionList: list.map((i) => ({ name: i.name, value: i.id })).sort((s, t) => sortList(s, t)),
        open: true,
      });
    },
    onError: () => {
      message.error("Fail to get Delegate List.");
    },
    onFinally: () => setDelegateToState({ loading: false }),
  });

  const { run: runSubmit } = useRequest(toDoListService.multipleDelegate, {
    manual: true,
    debounceWait: 300,
    debounceLeading: true,
    debounceTrailing: false,
    onBefore: () => setLoading(true),
    onSuccess: (response) => {
      const {
        result,
        isLoginUserNoPermission,
        isDelegateeNoPermission,
        delegateTo,
        isHaveSuccessful,
        failureDelegateVOs,
      } = response;
      if (isLoginUserNoPermission) {
        reloadMessage({ type: "error", message: "You don't have permission to handle the to-do." });
        window.location.reload();
        return;
      }
      if (isDelegateeNoPermission) {
        message.error(
          `${delegateTo} no longer has permission to handle the to-do. Please delegate the to-do to another person.`,
          5
        );
        setConfirmModalState({ showConfirmModal: false });
        setDelegateToState({ delegateToUserId: "" });
        return;
      }
      if (!result) {
        message.error(
          "You can not delegate this to-do to process initiator, the upstream approvers and someone that already have this to-do.",
          5
        );
        setConfirmModalState({ showConfirmModal: false });
        setDelegateToState({ delegateToUserId: "" });
        return;
      }

      setMessageState({
        showPostSubmitMessage: true,
        successFlag: isHaveSuccessful,
        failItems: failureDelegateVOs,
      });
    },
    onError: () => {
      message.error("Fail to update you action.");
    },
    onFinally: () => setLoading(false),
  });

  const handleConfirm = async () => {
    if (loading) return;
    runSubmit({
      currentNodeAccountId: selectedRows[0].currentNodeAccountId,
      procedureStepIDs: selectedRowKeys,
      delegateTo: delegateToState.delegateToUserId,
      delegatedBy: currentUser.identityUserInfo.englishName,
      reason: delegateToState.reasonText,
    });
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  useDebounceEffect(
    () => {
      if (isModalOpen) return;
      setDelegateToState({ ...initDelegateToState });
      setMessageState({ ...initMessageState });
      setConfirmModalState({ ...initConfirmModalState });
    },
    [isModalOpen],
    {
      wait: 100,
    }
  );

  const delegateToCancel = () => {
    setConfirmModalState({
      showConfirmModal: true,
      modalContent: "Are you sure to quit without saving?",
      continue: false,
    });
  };

  const delegateToConfirm = () => {
    if (!delegateToState.reasonText && isReasonRequired) {
      setDelegateToState({ reasonTextStatus: "error" });
      return;
    }
    setConfirmModalState({
      showConfirmModal: true,
      modalContent: "Are you sure to submit?",
      continue: true,
    });
  };

  const isReasonRequired = selectedRows.some(
    (item) =>
      item.processCategory === "Update Procedure" &&
      item.highlyRegulated === "Yes" &&
      item.actionRequired === "Approve"
  );

  const handleDropdownOpen = () => {
    runGetDelegateToList({
      procedureStepIDs: selectedRowKeys,
    });
  };

  const handleDropdownClose = () => {
    setDelegateToState({ open: false });
  };

  return (
    <Modal
      open={isModalOpen}
      closable={false}
      maskClosable={false}
      zIndex={1201}
      footer={null}
      bodyStyle={{ padding: "16px", paddingBottom: "10px" }}
      wrapClassName="batchDelegateModal"
      width={messageState.failItems.length > 0 ? 768 : 520}
    >
      {!messageState.showPostSubmitMessage && (
        <div className="mt-2 ml-2">
          {!confirmModalState.showConfirmModal && (
            <>
              <div className="w-[19.3rem] mb-[1.4375rem] flex justify-between items-center">
                <InputLabel text="Delegate To" className="delegateLabel" required />
                <NormalSelect
                  showSearch
                  optionList={delegateToState.optionList}
                  handleFilterOption={(input: any, option: any) =>
                    handleFilterOption(input, option)
                  }
                  handleChange={(value: string) => setDelegateToState({ delegateToUserId: value })}
                  value={delegateToState.delegateToUserId}
                  loading={delegateToState.loading}
                  onDropdownVisibleChange={(open: boolean) =>
                    open ? handleDropdownOpen() : handleDropdownClose()
                  }
                  open={delegateToState.open}
                  testid="batch-delegation-delegate-to-select"
                />
              </div>
              <div className="w-[29rem] flex justify-between">
                <InputLabel text="Reason" className="delegateLabel" required={isReasonRequired} />
                <NormalTextArea
                  width="22.2"
                  value={delegateToState.reasonText}
                  status={delegateToState.reasonTextStatus}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setDelegateToState({ reasonText: e.target.value, reasonTextStatus: "" })
                  }
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  className="delegateReasonText scrollExpendDiv"
                  testid="batch-delegation-reason-textarea"
                />
              </div>
              <div className="batchDelegateCustomFooter mt-[34px] flex justify-end gap-x-2">
                <Button data-testid="batch-delegation-cancel-btn" onClick={delegateToCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  disabled={!delegateToState.delegateToUserId}
                  onClick={delegateToConfirm}
                  data-testid="batch-delegation-confirm-btn"
                >
                  Confirm
                </Button>
              </div>
            </>
          )}
          {confirmModalState.showConfirmModal && (
            <>
              <Spin spinning={loading}>
                <span className="fs-16">{confirmModalState.modalContent}</span>
              </Spin>

              <div className="batchDelegateCustomFooter mt-[34px] flex justify-end gap-x-2">
                <Button
                  data-testid="batch-delegation-modal-cancel-btn"
                  onClick={() => setConfirmModalState({ showConfirmModal: false })}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  data-testid="batch-delegation-modal-confirm-btn"
                  onClick={() => {
                    if (confirmModalState.continue) {
                      handleConfirm();
                    } else {
                      closeModal();
                    }
                  }}
                >
                  Confirm
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {messageState.showPostSubmitMessage && (
        <PostDelegateToMessage
          messageState={messageState}
          closeModal={() => {
            useBatchDelegate.setState({ selectedRowKeys: [], selectedRows: [] });
            closeModal();
          }}
          delegateToName={
            delegateToState.optionList.find((i) => i.value === delegateToState.delegateToUserId)
              ?.name ?? ""
          }
        />
      )}
    </Modal>
  );
};

const PostDelegateToMessage = ({
  messageState,
  closeModal,
  delegateToName,
}: {
  messageState: Pick<MessageState, "failItems" | "successFlag">;
  closeModal: () => void;
  delegateToName: string;
}) => {
  const setFlushTable = useBatchDelegate((state) => state.setFlushTable);
  return (
    <div
      className={cn({
        "h-[98px] flex flex-col justify-between":
          messageState.successFlag && messageState.failItems.length === 0,
      })}
    >
      <div
        className={cn("mt-2 mx-2", {
          "mx-4": messageState.failItems.length > 0,
        })}
      >
        {messageState.successFlag && (
          <div className="text-base text-[#333333]">
            <span className="whitespace-nowrap">The to-do items have been delegated to</span>
            <span className="font-bold whitespace-nowrap">&nbsp;{delegateToName}&nbsp;</span>
            <span className="whitespace-nowrap">successfully!</span>
          </div>
        )}
        {messageState.failItems.length > 0 && (
          <div className="rounded bg-[#FF99210D] border border-solid border-[#FF9921] mt-4 p-5 text-base leading-[26px] text-[#333] max-h-80 overflow-y-auto custom-scrollbar">
            <span className="mb-3 inline-block">Some to-do items have already been processed:</span>
            <div className="gap-y-2 flex flex-col">
              {messageState.failItems.map((item) => (
                <WarningItem item={item} key={item.transactionId} />
              ))}
            </div>
          </div>
        )}
      </div>
      <div
        className={cn("batchDelegateCustomFooter mt-[34px] flex justify-end gap-x-2", {
          "mt-6 mx-4 mb-[14px]": messageState.failItems.length > 0,
          "mt-auto": messageState.successFlag && messageState.failItems.length === 0,
        })}
      >
        <Button
          type="primary"
          data-testid="batch-delegation-ok-btn"
          onClick={() => {
            closeModal();
            setFlushTable();
          }}
        >
          OK
        </Button>
      </div>
    </div>
  );
};

const WarningItem = ({ item }: { item: MessageState["failItems"][number] }) => {
  return (
    <div className="flex gap-x-2.5 items-center">
      <svg
        className="min-w-[5px] self-start mt-[11px]"
        width="5"
        height="6"
        viewBox="0 0 5 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="2.5" cy="3" r="2.5" fill="#1F1F1F" />
      </svg>
      <span className="whitespace-break-spaces">
        <span>{item.transactionId} -</span>
        <span className="font-bold">&nbsp;{item.taskLabelName}&nbsp;</span>
        <span>- {item.processCategory}</span>
      </span>
    </div>
  );
};
const __justForTest__ = {
  SelectAllCheckbox,
  DelegateComfirmModal,
};
export { useBatchDelegate, PostDelegateToMessage, __justForTest__ };

export default BatchDelegateButton;
